import { Navigate, useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Loader2, Building2, User } from 'lucide-react';
import { toast } from 'sonner';
import { useAuthContext } from '@/contexts/AuthContext';
import { criarContaConsumer } from '@/services/consumers.service';

export default function EscolherConta() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, isLoading, isConsumer, needsOnboarding, refreshConsumer } = useAuthContext();

  const criarCliente = useMutation({
    mutationFn: criarContaConsumer,
    onSuccess: async () => {
      await refreshConsumer();
      navigate('/app', { replace: true });
    },
    onError: (error: unknown) => {
      const msg = error instanceof Error ? error.message : '';
      toast.error(msg.includes('already_org_member') ? t('consumer.choose.alreadyCompany') : t('consumer.choose.error'));
    },
  });

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-foreground" />
      </div>
    );
  }
  if (!user) return <Navigate to="/auth" replace />;
  if (isConsumer) return <Navigate to="/app" replace />;
  if (!needsOnboarding) return <Navigate to="/painel" replace />;

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="w-full max-w-lg">
        <h1 className="text-2xl font-bold text-center mb-2">{t('consumer.choose.title')}</h1>
        <p className="text-sm text-muted-foreground text-center mb-8">{t('consumer.choose.subtitle')}</p>
        <div className="grid gap-4 sm:grid-cols-2">
          <button
            type="button"
            onClick={() => navigate('/onboarding')}
            disabled={criarCliente.isPending}
            className="ow-card p-6 flex flex-col items-center text-center gap-3 hover:border-primary transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Building2 className="h-10 w-10 text-primary" />
            <span className="font-bold">{t('consumer.choose.company')}</span>
            <span className="text-xs text-muted-foreground">{t('consumer.choose.companyDesc')}</span>
          </button>
          <button
            type="button"
            onClick={() => criarCliente.mutate()}
            disabled={criarCliente.isPending}
            className="ow-card p-6 flex flex-col items-center text-center gap-3 hover:border-primary transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {criarCliente.isPending ? <Loader2 className="h-10 w-10 animate-spin text-primary" /> : <User className="h-10 w-10 text-primary" />}
            <span className="font-bold">{t('consumer.choose.customer')}</span>
            <span className="text-xs text-muted-foreground">{t('consumer.choose.customerDesc')}</span>
          </button>
        </div>
      </div>
    </div>
  );
}
